import React, { useEffect, useState } from "react";
import { apiClient } from "../../api";
import { useProperty } from "../../Contexts/PropertyContext/PropertyContext";
import { baseUrl } from "../../Environments/environment";
import { useNavigate } from "react-router-dom";
import { formatPaymentAmount } from "./paymentCurrency.util";

export default function Transactions() {
  const [loading, setLoading] = useState(true);

  const [transactions, setTransactions] = useState([]);
  const [usageList, setUsageList] = useState([]);

  const [activeTab, setActiveTab] = useState("transactions");

  const [page, setPage] = useState(1);
  const limit = 10;

  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 1,
  });

  const navigate = useNavigate();
  const { selectedPropertyId } = useProperty();
  const propertyId = selectedPropertyId;

  // ---------------------------- LOAD DATA ---------------------------- //

  const loadData = async () => {
    try {
      setLoading(true);

      const endpoint =
        activeTab === "transactions"
          ? `${baseUrl}/stripe/transactions/${propertyId}`
          : `${baseUrl}/stripe/usage/${propertyId}`;

      const response = await apiClient.get(endpoint, {
        params: {
          page,
          limit,
        },
      });
      console.log(response);
      if (activeTab === "transactions") {
        setTransactions(response.data.result.result);
      } else {
        setUsageList(response.data.result.result);
      }

      setPagination(response.data.result.pagination);
    } catch (err) {
      console.error("Failed to load:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (propertyId) loadData();
  }, [propertyId, page, activeTab]);

  // ---------------------------- UTILS ---------------------------- //

  const formatAmount = (cents, currency = "usd") =>
    formatPaymentAmount(cents, currency);
  const formatDate = (ts) => new Date(ts).toLocaleString();

  const badge = (status) => {
    switch (status) {
      case "succeeded":
        return (
          <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-700">
            Succeeded
          </span>
        );
      case "failed":
        return (
          <span className="px-2 py-1 text-xs rounded bg-red-100 text-red-700">
            Failed
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 text-xs rounded bg-yellow-100 text-yellow-700">
            Pending
          </span>
        );
    }
  };

  // Reset search + page when switching tabs
  const switchTab = (tab) => {
    setActiveTab(tab);
    setPage(1);
  };

  // ---------------------------- PAGE UI ---------------------------- //

  return (
    <div className="">
      {/* Header */}
     

      {/* Tabs */}
      <div className="flex gap-3 sm:gap-4 border-b mb-3 sm:mb-4 overflow-x-auto mt-3" >
        <button
          className={`pb-2 text-sm sm:text-base whitespace-nowrap ${
            activeTab === "transactions"
              ? "border-b-2 border-blue-600 text-blue-600 font-medium"
              : "text-gray-600"
          }`}
          onClick={() => switchTab("transactions")}
        >
          Transactions
        </button>

        <button
          className={`pb-2 text-sm sm:text-base whitespace-nowrap ${
            activeTab === "usage"
              ? "border-b-2 border-blue-600 text-blue-600 font-medium"
              : "text-gray-600"
          }`}
          onClick={() => switchTab("usage")}
        >
          Usage
        </button>
      </div>

      {/* Loading Skeleton */}
      {loading ? (
        <div className="animate-pulse">
          {/* Skeleton for Table */}
          <div className="overflow-x-auto border rounded-md bg-white">
            <div className="min-w-full">
              {/* Skeleton Header */}
              <div className="bg-gray-50 border-b">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="flex-1 px-2 sm:px-4 py-2">
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Skeleton Rows */}
              {[...Array(5)].map((_, i) => (
                <div key={i} className="border-b">
                  <div className="flex">
                    {[...Array(5)].map((_, j) => (
                      <div key={j} className="flex-1 px-2 sm:px-4 py-3">
                        <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* Skeleton for Pagination */}
          <div className="flex items-center justify-between mt-4">
            <div className="h-10 bg-gray-200 rounded w-20"></div>
            <div className="h-4 bg-gray-200 rounded w-24"></div>
            <div className="h-10 bg-gray-200 rounded w-16"></div>
          </div>
        </div>
      ) : (
        <>
          {/* Table Container */}
          <div className="overflow-x-auto border rounded-md bg-white -mx-2 sm:mx-0">
            <div className="inline-block min-w-full align-middle">
              <table className="min-w-full text-xs sm:text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    {activeTab === "transactions" ? (
                      <>
                        <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-900">Type</th>
                        <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-900">Reviews</th>
                        <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-900">Amount</th>
                        <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-900">Status</th>
                        <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-900">Date</th>
                      </>
                    ) : (
                      <>
                        <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-900">Name</th>
                        <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-900">Reviews</th>
                        <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-900">Mobile No</th>
                        <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-900">Email</th>
                        <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-900">Date</th>
                      </>
                    )}
                  </tr>
                </thead>

                <tbody>
                  {activeTab === "transactions"
                    ? transactions.map((t) => (
                        <tr key={t._id} className="border-b hover:bg-gray-50">
                          <td className="px-2 sm:px-4 py-2 sm:py-3 capitalize whitespace-nowrap">
                            {t.type.replace("_", " ")}
                          </td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3 font-medium whitespace-nowrap">{t.credits}</td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap">{formatAmount(t.amountCents, t.currency)}</td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap">{badge(t.status)}</td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs">{formatDate(t.createdAt)}</td>
                        </tr>
                      ))
                    : usageList.map((u) => (
                        <tr key={u._id} className="border-b hover:bg-gray-50">
                          <td className="px-2 sm:px-4 py-2 sm:py-3 capitalize whitespace-nowrap">
                            {(u?.firstName ?? "") + " " + (u?.lastName ?? "")}
                          </td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3 font-medium whitespace-nowrap">1</td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap">{u.phoneNumber || "-"}</td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs sm:text-sm">{u.email || "-"}</td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs">{formatDate(u.createdAt)}</td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4 gap-2 sm:gap-4">
            <button
              disabled={page === 1}
              className="px-2 sm:px-3 py-2 text-xs sm:text-sm border rounded disabled:opacity-50 whitespace-nowrap"
              onClick={() => setPage(page - 1)}
            >
              Previous
            </button>

            <div className="text-xs sm:text-sm whitespace-nowrap">
              Page {page} of {pagination.totalPages}
            </div>

            <button
              disabled={page === pagination.totalPages}
              className="px-2 sm:px-3 py-2 text-xs sm:text-sm border rounded disabled:opacity-50 whitespace-nowrap"
              onClick={() => setPage(page + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}
